/**
 * Portfolio state and the add/remove button for one row on the Listings page.
 *
 * Only an `active` listing can appear on the public portfolio, so the control
 * renders nothing for any other status. The state comes from the same bucket
 * derivation as the status badge: "live" is on the portfolio, "draft" is not.
 *
 * The row is a `role="button"` card that opens the listing on click and on
 * Enter/Space, so the button stops both events from reaching it.
 */

import { useTranslation } from "react-i18next";
import type { Listing } from "@/features/realtor/services/listings-api.service";
import { deriveListingStatusBucket } from "@/features/realtor/utils/listing-status";
import { StudioButton } from "@/features/studio/theme";

export interface ListingPortfolioControlProps {
	listing: Listing;
	/** False when the realtor is known not to be verified, so publishing would be refused. */
	canAdd: boolean;
	/** A request for this listing is in flight. */
	pending: boolean;
	onAdd: () => void;
	onRemove: () => void;
}

export function ListingPortfolioControl({
	listing,
	canAdd,
	pending,
	onAdd,
	onRemove,
}: ListingPortfolioControlProps) {
	const { t } = useTranslation();
	if (listing.status !== "active") return null;

	const onPortfolio = deriveListingStatusBucket(listing) === "live";
	return (
		<div className="flex flex-wrap items-center justify-between gap-2 mt-3">
			<span className="text-xs text-dashboard-muted">
				{onPortfolio
					? t("realtor.listings.portfolio.onPortfolio")
					: t("realtor.listings.portfolio.notOnPortfolio")}
			</span>
			<StudioButton
				variant="secondary"
				size="sm"
				className="border-dashboard-border text-dashboard-text"
				disabled={pending || (!onPortfolio && !canAdd)}
				onClick={(e) => {
					e.stopPropagation();
					if (onPortfolio) onRemove();
					else onAdd();
				}}
				onKeyDown={(e) => e.stopPropagation()}
			>
				{onPortfolio
					? t("realtor.listings.portfolio.remove")
					: t("realtor.listings.portfolio.add")}
			</StudioButton>
		</div>
	);
}
